import React, { useState } from 'react';
import { EVIDENCE_SCALE } from '../../constants/adhdTriageOutcomes';
import { TIER_CONFIG, calculateIndicatorStats } from '../../utils/riskStratification';
import { tagSourceLabel } from '../../constants/evidenceTagging';

/**
 * Shared presentational pieces for the structured triage decision wizards.
 * AdhdTriageDecisionForm and AutismTriageDecisionForm compose these; each
 * pathway form owns its own step structure and payload.
 */

/**
 * Horizontal progress stepper: one numbered node per step, filled in the brand
 * colour up to (and including) the current step, joined by connectors. The
 * current step's short label sits under its node (the full title shows beneath
 * the stepper), keeping the row legible even with many steps.
 */
export function StepProgress({ labels, current }) {
  return (
    <ol className="flex items-start" aria-label="Progress">
      {labels.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={label}>
            <li className="flex flex-col items-center">
              <span
                aria-current={active ? 'step' : undefined}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                  done || active
                    ? 'bg-primary text-white'
                    : 'border border-gray-300 bg-white text-gray-400'
                } ${active ? 'ring-4 ring-primary/15' : ''}`}
              >
                {i + 1}
              </span>
              {active && (
                <span className="mt-1 hidden max-w-[6.5rem] text-center text-[11px] font-medium leading-tight text-primary sm:block">
                  {label}
                </span>
              )}
            </li>
            {i < labels.length - 1 && (
              <span
                className={`mx-2 mt-4 h-0.5 flex-1 rounded-full ${done ? 'bg-primary' : 'bg-gray-200'}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

/**
 * Respondent pill — colour-codes who gave an answer (the school vs the
 * parent/carer), mirroring the badge treatment used elsewhere on the platform.
 */
export function RespondentBadge({ respondent }) {
  const isSchool = /school/i.test(respondent);
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        isSchool
          ? 'bg-sky-50 text-sky-700 ring-sky-200'
          : 'bg-primary/10 text-primary ring-primary/20'
      }`}
    >
      {respondent}
    </span>
  );
}

/**
 * One surfaced answer, rendered as a compact card: the question it answers, a
 * respondent badge, and each selected value as its own chip (multi-select
 * options read as a row of tags rather than a comma-run of prose).
 */
export function SurfacedEvidence({ row }) {
  const items = row.values && row.values.length ? row.values : row.value ? [row.value] : [];
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-gray-600">{row.label}</p>
        {row.respondent ? <RespondentBadge respondent={row.respondent} /> : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * One graded evidence question: the shared four-option scale as a radio grid
 * plus an optional comment. `children` (when provided) render between the
 * scale and the comment affordance — the autism form slots its reported-by /
 * long-term sub-questions there.
 */
export function EvidenceQuestion({ question, answer, onSelect, onComment, children, scale = EVIDENCE_SCALE }) {
  const [commentOpen, setCommentOpen] = useState(false);
  const showComment = commentOpen || Boolean(answer?.comment);
  // The scale is caller-supplied so each pathway reads its own options: ADHD
  // uses the graded evidence scale (default); autism passes its impact scale
  // (TD-241). The grid stays responsive whatever the option count. The default
  // parameter only covers undefined, so guard explicitly: a null/empty scale
  // must fall back rather than crash the wizard mid-decision.
  const scaleOptions = Array.isArray(scale) && scale.length ? scale : EVIDENCE_SCALE;
  return (
    <fieldset className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-left">
      <legend className="px-1 text-sm font-medium text-gray-900">{question.label}</legend>
      {question.examples && (
        <p className="mt-1 text-xs text-gray-500">{question.examples}</p>
      )}
      <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {scaleOptions.map((option) => {
          const checked = answer?.value === option.id;
          return (
            // `relative` is load-bearing: the sr-only radio inside is
            // absolutely positioned, and without a positioned ancestor it
            // anchors to the top of the document - clicking an option then
            // focus-scrolls every scrollable ancestor towards the page top,
            // breaking the full-page wizard's scroll (window-within-a-window,
            // reported by UAT and clinical review).
            <label
              key={option.id}
              className={`relative flex cursor-pointer items-center justify-center rounded-md border p-2 text-center ${
                checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={question.key}
                className="sr-only"
                checked={checked}
                onChange={() => onSelect(option.id)}
              />
              <span className="text-sm text-gray-900">{option.label}</span>
            </label>
          );
        })}
      </div>
      {children}
      {question.supportsComment && (
        <div className="mt-2">
          {!showComment ? (
            <button
              type="button"
              onClick={() => setCommentOpen(true)}
              className="text-xs font-medium text-primary hover:text-primary/80"
            >
              Add comment
            </button>
          ) : (
            <textarea
              value={answer?.comment || ''}
              onChange={(e) => onComment(e.target.value)}
              rows={2}
              aria-label={`Comment — ${question.label}`}
              placeholder="Optional comment"
              className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
          )}
        </div>
      )}
    </fieldset>
  );
}

export function ToggleChoice({ name, label, checked, onChange }) {
  return (
    // `relative` is load-bearing - see the option label in EvidenceQuestion.
    <label
      className={`relative flex cursor-pointer items-center justify-center rounded-md border px-5 py-2 ${
        checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <input type="radio" name={name} className="sr-only" checked={checked} onChange={onChange} />
      <span className="text-sm font-medium text-gray-900">{label}</span>
    </label>
  );
}

export function PatientProfileStrip({ subject }) {
  const sex = subject?.sex || '';
  const gender = subject?.gender || '';
  const dob = subject?.dob || '';
  // Age changes over time so it is not stored — derive from DOB when the
  // record carries no explicit age (mirrors the patient overview header).
  const deriveAge = (dobStr) => {
    if (!dobStr) return null;
    const d = new Date(dobStr);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const md = now.getMonth() - d.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < d.getDate())) a -= 1;
    return a >= 0 && a < 150 ? a : null;
  };
  const age = subject?.age ?? deriveAge(dob);

  // The date of birth sits in the page's shared identity header (TDI-392),
  // so the strip carries what that header does not: sex, gender and age.
  const items = [
    { label: 'Sex', value: sex },
    { label: 'Gender', value: gender },
    { label: 'Age', value: age !== null && age !== undefined ? String(age) : '' },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <dl className="grid grid-cols-3 gap-x-6 gap-y-1">
        {items.map((item) => (
          <div key={item.label} className="text-left">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {item.label}
            </dt>
            <dd className="text-sm text-gray-900">{item.value || 'Not recorded'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function RiskStratificationSummary({ riskStratification }) {
  if (!riskStratification?.tier && !riskStratification?.tierLabel) {
    return (
      <p className="mt-1 text-xs text-gray-500">
        No automated risk stratification is available for this referral.
      </p>
    );
  }
  const tierConfig = TIER_CONFIG[riskStratification.tier] || null;
  const tierLabel = riskStratification.tierLabel || tierConfig?.label || '';
  const stats = calculateIndicatorStats(riskStratification.domainSummaries);
  return (
    <div className="mt-2 text-left">
      <span
        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
        style={
          tierConfig
            ? {
                backgroundColor: tierConfig.bgColor,
                color: tierConfig.captionColor,
                border: `1px solid ${tierConfig.borderColor}`,
              }
            : undefined
        }
      >
        {tierLabel}
      </span>
      {stats.highestLevel && (
        <p className="mt-1 text-xs text-gray-600">
          {stats.highestLevelCount} {stats.highestLevel.toLowerCase()} risk{' '}
          {stats.highestLevelCount === 1 ? 'indicator' : 'indicators'} reported across the
          referral responses.
        </p>
      )}
    </div>
  );
}

/**
 * Resolve a clinician's real name, or '' when none is known. Used for the
 * persisted audit record so we never write a placeholder the clinician did
 * not enter.
 */
export function clinicianName(clinician) {
  return (
    clinician?.clinicianName ||
    [clinician?.firstName, clinician?.lastName].filter(Boolean).join(' ') ||
    ''
  );
}

/** Display variant: falls back to a readable placeholder for the UI only. */
export function clinicianDisplayName(clinician) {
  return clinicianName(clinician) || 'Unnamed clinician';
}

/**
 * Lines the clinician tagged in the history review, shown on the decision
 * screen that matches their kind (TDI-394). Each line says whose answer it is,
 * repeats the tagged words, and carries the clinician's note when they wrote
 * one. Nothing here was chosen by the platform: these are the clinician's own
 * tags, and an empty list renders nothing (R2, no "not flagged" list).
 *
 * Props:
 *   - heading: shown above the list, or omitted
 *   - tags: the tags to list, in display order
 *   - onRemoveTag?(tagId): shows a Remove button on each line
 *   - removeLabel(tag): the Remove button's accessible name
 *   - firstReview: labels every line "First review" in the second-review
 *     style, with the note introduced as the first reviewer's (R-008)
 */
export function TaggedLines({ heading, tags = [], onRemoveTag, removeLabel, firstReview = false }) {
  if (!tags.length) return null;
  return (
    <div className="mt-3 text-left">
      {heading && <p className="text-xs font-medium text-gray-600">{heading}</p>}
      <ul className="mt-1.5 space-y-1.5" aria-label={heading || undefined}>
        {tags.map((tag, i) => {
          const source = tagSourceLabel(tag.source) || tag.source;
          const note = (tag.note || '').trim();
          return (
            <li
              key={tag.id || `${tag.label}-${i}`}
              className={`flex items-start justify-between gap-2 rounded-md border px-2.5 py-1.5 ${
                firstReview ? 'border-amber-200 bg-amber-50' : 'border-primary/20 bg-primary/5'
              }`}
            >
              <div className="text-xs text-gray-800">
                <p>
                  {firstReview && <span className="font-semibold text-amber-900">First review: </span>}
                  {source && <span className="font-medium">{source}</span>}
                  {source && ' — '}
                  {tag.label}{tag.value ? `: ${tag.value}` : ''}
                </p>
                {note && (
                  <p className="mt-0.5 text-gray-700">
                    {firstReview ? `Note: ${note}` : `Your note: ${note}`}
                  </p>
                )}
              </div>
              {onRemoveTag && tag.id && (
                <button
                  type="button"
                  onClick={() => onRemoveTag(tag.id)}
                  className="text-xs text-primary/70 hover:text-primary"
                  aria-label={removeLabel ? removeLabel(tag) : `Remove tag — ${tag.label}`}
                >
                  Remove
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
